import { useMutation } from '@tanstack/react-query'

import { useSharedDates } from '@contexts/sharedDates'
import { useDate } from '@hooks/dates'
import { IGenericTask } from '@interfaces/task/generic/IGenericTask'
import { queryClient } from '@services/queryClient'

import { useDeleteTask } from './delete'

export function useOptimisticDeleteTask() {
  const { deleteTask } = useDeleteTask()
  const { selectedDate } = useSharedDates()
  const { getWeekRange } = useDate()

  const { weekStart } = getWeekRange(selectedDate)

  return useMutation({
    mutationFn: deleteTask,
    onMutate: handleOptimisticTaskListUpdate,
    onError: (_err, _newTask, context) => {
      if (!context?.tasksKey) return

      const { previousTasks, tasksKey } = context
      queryClient.setQueryData(tasksKey, previousTasks)
    },
    onSettled: (_tasks, _err, _variables, context) =>
      queryClient.invalidateQueries({
        queryKey: context?.tasksKey,
      }),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ['TASKS', weekStart.getTime()],
        type: 'active',
      })
    },
  })

  async function handleOptimisticTaskListUpdate(taskId: number) {
    await queryClient.cancelQueries({
      queryKey: ['TASKS', weekStart.getTime()],
    })
    const queries = queryClient.getQueriesData<IGenericTask[]>({
      queryKey: ['TASKS', weekStart.getTime()],
      type: 'active',
    })

    const tasksQuery = queries[0]
    if (!tasksQuery) return { tasksKey: undefined, previousTasks: undefined, updatedTasks: undefined }

    const [tasksKey, tasksResult] = tasksQuery
    const previousTasks = tasksResult

    const updatedTasks = tasksResult?.filter((oldTask) => oldTask.id !== taskId)

    queryClient.setQueryData(tasksKey, () => updatedTasks)

    return {
      tasksKey,
      previousTasks,
      updatedTasks,
    }
  }
}
